import { useMemo } from 'react';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useCustomWorklists } from './useCustomWorklists';
import { useIsFilterPersistenceDisabled } from './useIsFilterPersistenceDisabled';
import { getWorklistKey } from '../utils/worklistUtils';
import { SortColumn, SortOrder } from 'generated/graphql';
import { WorklistColumnSortPreference } from '../types';
import { convertWorklistFiltersToState } from '../../WorklistFilters/utils';
import { isEmptyStringOrNull } from 'utils/string';
import { WithApolloContext } from 'modules/Apollo/types';

const defaultPreferences = {
  columns: [],
  sorting: [],
  filters: [],
};

// Util functions to convert between sorting formats
export const convertWorklistSortingToTableSorting = (
  worklistSorting: Array<WorklistColumnSortPreference>
): { sortColumns: SortColumn[]; sortOrders: SortOrder[] } => {
  const sortColumns = worklistSorting.map((sort) => sort.column as SortColumn);
  const sortOrders = worklistSorting.map((sort) => (sort.desc ? SortOrder.Desc : SortOrder.Asc));
  return { sortColumns, sortOrders };
};

export const convertTableSortingToWorklistSorting = (
  sortColumns: SortColumn[],
  sortOrders: SortOrder[]
): Array<WorklistColumnSortPreference> => {
  return sortColumns.map((column, index) => ({
    column,
    desc: sortOrders[index] === SortOrder.Desc,
  }));
};

export const usePublicWorklistPreferences = ({
  worklistIds,
  context,
  skipQueries = false,
}: {
  worklistIds: string[];
  skipQueries?: boolean;
} & WithApolloContext) => {
  const { data } = useCurrentUser({
    context,
    skip: skipQueries || worklistIds.length === 0,
  });

  return useMemo(() => {
    if (worklistIds.length === 0) return null;

    // combine worklist IDs into a single key for combined worklists
    const worklistKey = worklistIds.length > 1 ? getWorklistKey(worklistIds) : worklistIds[0];

    return (
      data?.me?.worklistSettings?.worklistPreferences?.find(
        (pref) => pref.worklistKey === worklistKey
      ) ?? defaultPreferences
    );
  }, [worklistIds, data?.me?.worklistSettings?.worklistPreferences]);
};

export const usePrivateWorklistPreferences = ({
  worklistId,
  context,
  skipQueries = false,
}: {
  worklistId: string;
  skipQueries?: boolean;
} & WithApolloContext) => {
  const { privateWorklists } = useCustomWorklists({
    queryOptions: { context, skip: skipQueries || isEmptyStringOrNull(worklistId) },
  });

  return useMemo(() => {
    const worklist = privateWorklists.get(worklistId);
    return worklist?.advancedSettings?.worklistPreferences || defaultPreferences;
  }, [privateWorklists, worklistId]);
};

export const useCustomWorklistPreferences = ({
  worklistIds,
  isPublicOrCombined,
  context,
  skipQueries = false,
}: {
  worklistIds: string[];
  isPublicOrCombined: boolean;
  skipQueries?: boolean;
} & WithApolloContext) => {
  const publicSettings = usePublicWorklistPreferences({
    worklistIds,
    context,
    skipQueries,
  });
  const privateSettings = usePrivateWorklistPreferences({
    worklistId: worklistIds[0] || '',
    context,
    skipQueries,
  });

  return useMemo(() => {
    return isPublicOrCombined ? publicSettings : privateSettings;
  }, [isPublicOrCombined, publicSettings, privateSettings]);
};

export const useCustomWorklistSortingPreferences = ({
  worklistIds,
  isPublicOrCombined,
  context,
  skipQueries = false,
}: {
  worklistIds: string[];
  isPublicOrCombined: boolean;
  skipQueries?: boolean;
} & WithApolloContext) => {
  const preferences = useCustomWorklistPreferences({
    worklistIds,
    isPublicOrCombined,
    context,
    skipQueries,
  });

  return useMemo(() => {
    if (!preferences?.sorting || preferences.sorting.length === 0) {
      return null;
    }

    const validSorting: Array<WorklistColumnSortPreference> = preferences.sorting
      .filter(
        (sort): sort is WorklistColumnSortPreference =>
          sort != null && sort.column != null && sort.desc != null
      )
      .map((sort) => ({ column: sort!.column, desc: sort!.desc }));

    if (validSorting.length === 0) {
      return null;
    }

    return convertWorklistSortingToTableSorting(validSorting);
  }, [preferences?.sorting]);
};

export const useCustomWorklistFilterPreferences = ({
  worklistIds,
  isPublicOrCombined,
  context,
  skipQueries = false,
}: {
  worklistIds: string[];
  isPublicOrCombined: boolean;
  skipQueries?: boolean;
} & WithApolloContext) => {
  const preferences = useCustomWorklistPreferences({
    worklistIds,
    isPublicOrCombined,
    context,
    skipQueries,
  });
  const isStickyFiltersDisabled = useIsFilterPersistenceDisabled({ context, skip: skipQueries });

  return useMemo(() => {
    if (isStickyFiltersDisabled) {
      return null;
    }
    if (!preferences?.filters || preferences.filters.length === 0) {
      return null;
    }

    const validFilters = preferences.filters
      .filter((filter) => {
        return (
          filter != null &&
          !isEmptyStringOrNull(filter.name) &&
          Array.isArray(filter.values) &&
          filter.values.length > 0
        );
      })
      .map((filter) => ({
        // Map to clean format as FilterDefinition can have non-nullable values
        name: filter!.name,
        values: [...filter!.values],
      }))
      .filter((filter) => filter.values.length > 0);

    if (validFilters.length === 0) {
      return null;
    }

    return convertWorklistFiltersToState(validFilters);
  }, [isStickyFiltersDisabled, preferences?.filters]);
};
